import { openDB, IDBPDatabase } from 'idb'

export interface IndexedDBConfig {
  dbName: string
  version: number
  storeName: string
  keyPath?: string
}

// Database instance cache to avoid multiple connections
const dbInstances = new Map<string, Promise<IDBPDatabase>>()

/**
 * Initialize IndexedDB database
 */
export const initIndexedDB = async (
  config: IndexedDBConfig,
): Promise<IDBPDatabase> => {
  // This function needs to be async because:
  // 1. openDB() returns a Promise<IDBPDatabase> - IndexedDB operations are async by design
  // 2. We need to wait for the database connection to be established
  // 3. The upgrade callback may need to perform async operations when creating object stores

  const key = `${config.dbName}_${config.version}_${config.storeName}`

  if (!dbInstances.has(key)) {
    const dbPromise = openDB(config.dbName, config.version, {
      upgrade: db => {
        if (!db.objectStoreNames.contains(config.storeName)) {
          db.createObjectStore(config.storeName, {
            keyPath: config.keyPath || 'id',
          })
        }
      },
    })
    dbInstances.set(key, dbPromise)
  }

  return dbInstances.get(key)!
}

/**
 * Get data from IndexedDB
 */
export const getFromIndexedDB = async <T = any>(
  config: IndexedDBConfig,
  key: string,
): Promise<T | null> => {
  try {
    const db = await initIndexedDB(config)
    return (await db.get(config.storeName, key)) || null
  } catch (error) {
    console.error('IndexedDB get error:', error)
    return null
  }
}

/**
 * Set data in IndexedDB
 */
export const setInIndexedDB = async <T = any>(
  config: IndexedDBConfig,
  key: string,
  data: T,
): Promise<void> => {
  try {
    const db = await initIndexedDB(config)
    const record = config.keyPath
      ? { [config.keyPath]: key, ...data }
      : { id: key, ...data }
    await db.put(config.storeName, record)
  } catch (error) {
    console.error('IndexedDB set error:', error)
  }
}

/**
 * Delete data from IndexedDB
 */
export const deleteFromIndexedDB = async (
  config: IndexedDBConfig,
  key: string,
): Promise<void> => {
  try {
    const db = await initIndexedDB(config)
    await db.delete(config.storeName, key)
  } catch (error) {
    console.error('IndexedDB delete error:', error)
  }
}

/**
 * Get all data from IndexedDB store
 */
export const getAllFromIndexedDB = async <T = any>(
  config: IndexedDBConfig,
): Promise<T[]> => {
  try {
    const db = await initIndexedDB(config)
    return await db.getAll(config.storeName)
  } catch (error) {
    console.error('IndexedDB getAll error:', error)
    return []
  }
}

/**
 * Clear all data from IndexedDB store
 */
export const clearIndexedDB = async (
  config: IndexedDBConfig,
): Promise<void> => {
  try {
    const db = await initIndexedDB(config)
    await db.clear(config.storeName)
  } catch (error) {
    console.error('IndexedDB clear error:', error)
  }
}
